import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from "@mui/icons-material";
import { Chip, CircularProgress } from "@mui/material";
import type { JobStatus, ReportStatus } from "@/types/marketReport";

type Status = ReportStatus | JobStatus;

type NormalizedStatus = "pending" | "processing" | "completed" | "failed";

function normalizeStatus(status: Status): NormalizedStatus {
  switch (status) {
    case "uploaded":
    case "queued":
      return "pending";
    case "processing":
    case "running":
      return "processing";
    case "processed":
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    default:
      return "pending";
  }
}

const STATUS_LABELS: Record<NormalizedStatus, string> = {
  pending: "Pending",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
};

function getStatusIcon(status: NormalizedStatus, iconSize: number) {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ fontSize: iconSize }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: iconSize }} />;
    case "processing":
      return <CircularProgress size={iconSize} />;
    case "pending":
      return <HourglassEmpty sx={{ fontSize: iconSize }} />;
    default:
      return null;
  }
}

function getStatusChipSx(status: NormalizedStatus) {
  switch (status) {
    case "completed":
      return {
        backgroundColor: "#e6f6ef",
        color: "#0f6b45",
      };
    case "processing":
      return {
        backgroundColor: "#e7f1ff",
        color: "#0d4f8b",
      };
    case "pending":
      return {
        backgroundColor: "#fff5e6",
        color: "#8a5200",
      };
    case "failed":
      return {
        backgroundColor: "#feeceb",
        color: "#9a2218",
      };
    default:
      return {
        backgroundColor: "#eef1f4",
        color: "#4a5565",
      };
  }
}

export function ReportStatusBadge({
  status,
  compact = false,
}: {
  status: Status;
  compact?: boolean;
}) {
  const normalized = normalizeStatus(status);
  const iconSize = compact ? 14 : 16;

  return (
    <Chip
      icon={getStatusIcon(normalized, iconSize) ?? undefined}
      label={STATUS_LABELS[normalized]}
      size="small"
      sx={{
        ...getStatusChipSx(normalized),
        borderRadius: "999px",
        fontWeight: 500,
        fontSize: compact ? "0.65rem" : "0.76rem",
        letterSpacing: 0,
        height: compact ? 22 : undefined,
        px: compact ? 0.125 : 0.25,
        "& .MuiChip-icon": {
          color: "inherit",
          ml: compact ? 0.5 : 0.75,
        },
        "& .MuiChip-label": {
          px: compact ? 0.75 : 1,
        },
      }}
    />
  );
}
